import Protomux from 'protomux'
import c from 'compact-encoding'

/*
 * Noticing a connection that has died without saying so.
 *
 * A native connection finds out on its own: UDX retransmits, gives up, and the
 * stream errors. A browser's connection is a relayed one, and it only ever
 * learns that the far end is gone if the relay tells it. The relay has had a
 * bug in exactly that (see server/relay/lib/relay-fixes.js), and a relay can
 * be restarted, overloaded or simply older than the fix — so a browser cannot
 * rest the health of every chat on that one message arriving.
 *
 * So each side says "still here" every PING_EVERY over a small channel of its
 * own, and a relayed connection whose peer has opened that channel — proof
 * that it would be pinging — and has then sent nothing at all for DEAD_AFTER
 * is closed. Closing it is what lets Hyperswarm find the peer again, and what
 * frees whatever was waiting on it.
 *
 * DEAD_AFTER is generous on purpose. A tab in the background has its timers
 * throttled to about one a minute, and a false alarm costs a reconnect; a
 * phone that has really gone costs a few minutes at most.
 *
 * A peer on an older build never opens the channel and is never judged.
 */

const PROTOCOL = 'pine/alive/1'

export const PING_EVERY = 25 * 1000
export const DEAD_AFTER = 150 * 1000

export function watchConnection (conn, { judge = false, now = Date.now, pingEvery = PING_EVERY, deadAfter = DEAD_AFTER } = {}) {
  let heard = now()
  let peerPings = false
  let ping = null
  let timer = null

  const onData = () => { heard = now() }
  conn.on('data', onData)

  let mux
  try {
    mux = Protomux.from(conn)
  } catch {
    conn.off('data', onData)
    return () => {}
  }

  const open = () => {
    const channel = mux.createChannel({
      protocol: PROTOCOL,
      onopen: () => { peerPings = true }
    })
    if (!channel) return // already open on this connection
    ping = channel.addMessage({ encoding: c.uint, onmessage: onData })
    channel.open()
  }

  mux.pair({ protocol: PROTOCOL }, open)
  open()

  const stop = () => {
    if (timer) clearInterval(timer)
    timer = null
    conn.off('data', onData)
    try { mux.unpair({ protocol: PROTOCOL }) } catch {}
  }

  timer = setInterval(() => {
    if (conn.destroying || conn.destroyed) return stop()
    try { if (ping) ping.send(0) } catch { /* closing */ }
    if (judge && peerPings && now() - heard > deadAfter) {
      stop()
      conn.destroy(new Error('The connection went silent'))
    }
  }, pingEvery)
  if (timer.unref) timer.unref()

  conn.once('close', stop)
  return stop
}
